import { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useSectorInsightsStocks } from './useSectorInsights';
import { formatMarketCapKrw, formatScore, formatSourceLabel } from './format';
import { STOCKS_PAGE_SIZE, type SectorInsightsStockItem } from './types';
import { Pager } from '../../components/Pager';

type SortKey = 'symbol' | 'name' | 'stock_score' | 'market_cap';
type SortDir = 'asc' | 'desc';

interface Props {
  canonicalKey: string;
}

export function SectorInsightsStocksTab({ canonicalKey }: Props): JSX.Element {
  const [page, setPage] = useState(0);
  const { data, isLoading, isError, error } =
    useSectorInsightsStocks(canonicalKey, page, STOCKS_PAGE_SIZE);

  // 정렬: default 스톡 스코어 DESC. 서버가 이미 그렇게 내려도 client-side 재정렬은 idempotent.
  const [sortKey, setSortKey] = useState<SortKey>('stock_score');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const items = data?.items ?? [];
  const total = data?.total_count ?? 0;

  const sorted = useMemo(() => {
    const copy = [...items];
    copy.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      let cmp: number;
      if (av === null && bv === null) cmp = 0;
      else if (av === null) cmp = 1; // null 은 항상 뒤로
      else if (bv === null) cmp = -1;
      else if (typeof av === 'string' && typeof bv === 'string') {
        cmp = av.localeCompare(bv, 'ko-KR');
      } else {
        cmp = (av as number) - (bv as number);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [items, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      const isNumeric = key === 'stock_score' || key === 'market_cap';
      setSortDir(isNumeric ? 'desc' : 'asc');
    }
  };

  return (
    <>
      <div className="pca-table-wrap">
        <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          <table className="pca-table" data-testid="sector-insights-stocks">
            <thead>
              <tr>
                <SortableTh label="심볼" k="symbol" cur={sortKey} dir={sortDir} onClick={handleSort} width={100} />
                <SortableTh label="종목명" k="name" cur={sortKey} dir={sortDir} onClick={handleSort} />
                <SortableTh label="스톡 스코어" k="stock_score" cur={sortKey} dir={sortDir} onClick={handleSort} width={120} align="right" />
                <SortableTh label="시가총액" k="market_cap" cur={sortKey} dir={sortDir} onClick={handleSort} width={140} align="right" />
                <th style={{ width: 340 }}>원천</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={5} className="pca-empty">불러오는 중…</td>
                </tr>
              )}
              {isError && !isLoading && (
                <tr>
                  <td colSpan={5} className="pca-empty">
                    종목 목록 로드 실패: {(error as Error)?.message ?? 'unknown'}
                  </td>
                </tr>
              )}
              {!isLoading && !isError && sorted.length === 0 && (
                <tr>
                  <td colSpan={5} className="pca-empty">표시할 종목이 없습니다.</td>
                </tr>
              )}
              {sorted.map((it) => (
                <StockRow key={it.symbol} item={it} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Pager page={page} pageSize={STOCKS_PAGE_SIZE} total={total} onPageChange={setPage} />
    </>
  );
}

function StockRow({ item }: { item: SectorInsightsStockItem }): JSX.Element {
  const sources = item.sources ?? [];
  return (
    <tr>
      <td><code>{item.symbol}</code></td>
      <td>{item.name}</td>
      <td style={{ textAlign: 'right' }}>{formatScore(item.stock_score)}</td>
      <td style={{ textAlign: 'right' }}>{formatMarketCapKrw(item.market_cap)}</td>
      <td>
        {sources.length === 0 ? (
          <span className="pca-muted">—</span>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {sources.map((s, idx) => (
              <span
                key={`${s.source}:${s.source_sector_name}:${idx}`}
                className="pca-status-badge"
                title={`${formatSourceLabel(s.source)} · ${s.source_sector_name}`}
              >
                {formatSourceLabel(s.source)} · {s.source_sector_name}
              </span>
            ))}
          </div>
        )}
      </td>
    </tr>
  );
}

function SortableTh({
  label,
  k,
  cur,
  dir,
  onClick,
  width,
  align,
}: {
  label: string;
  k: SortKey;
  cur: SortKey;
  dir: SortDir;
  onClick: (k: SortKey) => void;
  width?: number;
  align?: 'left' | 'right';
}): JSX.Element {
  const active = k === cur;
  return (
    <th
      style={{
        width,
        cursor: 'pointer',
        userSelect: 'none',
        textAlign: align ?? 'left',
      }}
      onClick={() => onClick(k)}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {label}
        {active &&
          (dir === 'asc' ? (
            <ChevronUp size={12} strokeWidth={1.75} aria-hidden />
          ) : (
            <ChevronDown size={12} strokeWidth={1.75} aria-hidden />
          ))}
      </span>
    </th>
  );
}
